/* v8 ignore start */
import {LottieKeyFrameModel} from "@/lib/lottie/animatedProperties/lottieKeyframeModel";
import {LottieLayerType} from "@/lib/lottie/layers/lottieLayerType";
import {LottieVisualLayerModel} from "@/lib/lottie/layers/lottieVisualLayerModel";
import {Effect, Fit} from "@/lib/lottie/layers/photoEffects";
import {PhotoKeyFrames} from "@/lib/lottie/layers/photoKeyFrames";
import {PhotoModel} from "@/models/photoModel";
import {RectangleModel} from "@/models/rectangleModel";

const FRAME_RATE = 24;

export class LottieImageLayerModel extends LottieVisualLayerModel<LottieImageLayerModel.IJson> {
  public get refId(): string {
    return this.json.refId;
  }

  public get metadata(): Maybe<IMetadata> {
    return this.json.meta;
  }

  public fitPhoto(): this {
    return this.update({
      ks: this.transform
        .update({
          a: {a: 0, k: [0, 0, 0], ix: 1, l: 2},
          p: {a: 0, k: [0, 0, 0], ix: 2},
          s: {a: 0, k: [100, 100, 100], ix: 6, l: 2},
        })
        .toObject(),
    });
  }

  public fitAnimatedPhoto(photo: PhotoModel, slotWidth: number, slotHeight: number): this {
    const duration = this.totalFrames;

    const photoWidth = photo.largestThumbnailWidth;
    const photoHeight = photo.largestThumbnailHeight;

    const slotRectangle = new RectangleModel({
      x: 0,
      y: 0,
      width: slotWidth,
      height: slotHeight,
    });

    let keyFrames = new PhotoKeyFrames({
      startPosition: {x: slotWidth / 2, y: slotHeight / 2},
      endPosition: {x: slotWidth / 2, y: slotHeight / 2},
      startScale: {x: 100, y: 100},
      endScale: {x: 100, y: 100},
    });

    const scale = 1;
    const anchorX = photoWidth / 2;
    const anchorY = photoHeight / 2;

    const fit = this.pickFit(photo);
    const possibleEffects = this.pickEffects(photo, fit);

    let effect = this.randomFrom(possibleEffects);

    keyFrames = keyFrames.fitPhoto(photo, fit, slotWidth, slotHeight);

    const panOffset = this.computePanOffset(slotHeight);

    // Rewrite the direction of the pan based on focus point
    if (effect === Effect.PanTowardsFocusPoint) {
      effect = this.randomFrom(keyFrames.pickPossiblePanDirections(photo));
    }

    switch (effect) {
      case Effect.ZoomInCenter:
        keyFrames = keyFrames.zoomAtTheEnd(this.zoomFactor);
        break;
      case Effect.ZoomOutCenter:
        keyFrames = keyFrames.zoomAtTheStart(this.zoomFactor).zoomAtTheEnd(scale);
        break;
      case Effect.ZoomInTowardsFocusPoint:
        keyFrames = keyFrames.zoomTowardsFocusPoint(photo, photo.focusPoint, slotRectangle, this.zoomFactor);
        break;
      case Effect.ZoomOutFromFocusPoint:
        keyFrames = keyFrames
          .zoomTowardsFocusPoint(photo, photo.focusPoint, slotRectangle, this.zoomFactor, true)
          .update({
            endScale: keyFrames.endScale,
          });
        break;
      case Effect.ZoomInTowardsSalientBox:
        keyFrames = keyFrames.zoomTowardsFocusPoint(photo, photo.salientFocusPoint, slotRectangle, this.zoomFactor);
        break;
      case Effect.ZoomOutFromSalientBox:
        keyFrames = keyFrames
          .zoomTowardsFocusPoint(photo, photo.salientFocusPoint, slotRectangle, this.zoomFactor, true)
          .update({
            endScale: keyFrames.endScale,
          });
        break;
      case Effect.PanLeft:
        // TODO: How to handle pan without zoom ???, improve the logic to not pick pan if photos at the edges ???
        keyFrames = keyFrames.zoom(this.zoomFactor).panLeft(panOffset);
        break;
      case Effect.PanRight:
        keyFrames = keyFrames.zoom(this.zoomFactor).panRight(panOffset);
        break;
      case Effect.PanDown:
        keyFrames = keyFrames.zoom(this.zoomFactor).panDown(panOffset);
        break;
      case Effect.PanUp:
        keyFrames = keyFrames.zoom(this.zoomFactor).panUp(panOffset);
        break;
    }

    const scaleKeyFrames: LottieKeyFrameModel.IJson[] = [
      {
        i: {x: [0.667, 0.667, 0.667], y: [1, 1, 1]}, // easeOut
        o: {x: [0.333, 0.333, 0.333], y: [0, 0, 0]}, // easeOut
        t: 0,
        s: [keyFrames.startScaleX, keyFrames.startScaleY, 100],
      },
      {t: duration, s: [keyFrames.endScaleX, keyFrames.endScaleY, 100]},
    ];

    const positionKeyFrames: LottieKeyFrameModel.IJson[] = [
      {
        i: {x: [0.667, 0.667, 0.667], y: [1, 1, 1]}, // easeOut
        o: {x: [0.333, 0.333, 0.333], y: [0, 0, 0]}, // easeOut
        t: 0,
        s: [keyFrames.startPositionX, keyFrames.startPositionY, 0],
      },
      {
        t: duration,
        s: [Math.round(keyFrames.endPositionX), Math.round(keyFrames.endPositionY), 0],
      },
    ];

    return this.update({
      meta: {...this.json.meta, effect, fit},
      ks: this.transform
        .update({
          a: {a: 0, k: [anchorX, anchorY, 0], ix: 1, l: 2},
          s: {a: 1, k: scaleKeyFrames, ix: 2, l: 2},
          p: {a: 1, k: positionKeyFrames, ix: 2},
          r: {a: 0, k: photo.rotation, ix: 12},
        })
        .toObject(),
    });
  }

  private pickEffects(photo: PhotoModel, fit: Fit): Effect[] {
    if (photo.hasFaces) {
      const effects = [];

      // zoom towards focus point works only when cover
      if (fit === Fit.Cover) {
        effects.push(...[Effect.ZoomInTowardsFocusPoint, Effect.ZoomOutFromFocusPoint]);
        effects.push(Effect.PanTowardsFocusPoint);
      } else {
        effects.push(...[Effect.ZoomInCenter, Effect.ZoomOutCenter]);

        const panEffects = photo.isPortrait ? [Effect.PanDown, Effect.PanUp] : [Effect.PanLeft, Effect.PanRight];
        effects.push(...panEffects);
      }

      return effects;
    } else {
      const effects = [Effect.ZoomInCenter, Effect.ZoomOutCenter];

      const panEffects = photo.isPortrait ? [Effect.PanDown, Effect.PanUp] : [Effect.PanLeft, Effect.PanRight];
      effects.push(...panEffects);

      return effects;
    }
  }

  // TODO: Improve this logic (ex: faces at the edges, consider effect/zoom)
  private pickFit(photo: PhotoModel): Fit {
    return photo.isPortrait ? Fit.Contain : Fit.Cover;
  }

  private randomFrom(effects: Effect[]): Effect {
    return effects[Math.floor(Math.random() * effects.length)];
  }

  private get longEffect(): boolean {
    return this.totalFrames > FRAME_RATE * 2;
  }

  private get zoomFactor(): number {
    return this.longEffect ? 1.2 : 1.1; // TODO: Improve the computation based on speed and other parameters
  }

  private computePanOffset(slotHeight: number): number {
    return (slotHeight / 2) * (this.zoomFactor - 1); // TODO: Improve the computation based on speed and other parameters
  }
}

interface IMetadata {
  uuid: string;
  effect?: string;
  fit?: string;
  static?: boolean;
}

export namespace LottieImageLayerModel {
  export interface IJson extends LottieVisualLayerModel.IJson {
    /** Layer type */
    ty: LottieLayerType.Image;

    /** ID of the image as specified in the assets */
    refId: string;

    /** Mixbook metadata used for debugging */
    meta: IMetadata;
  }
}
/* v8 ignore stop */
